/**
 * The clinician shell — one header, one navigation, one sign-out.
 *
 * Before this, each clinician screen carried its own ad-hoc link and only the
 * dashboard had a sign-out button, so from inside the prescription builder
 * there was no way out except the browser's back button. A session that cannot
 * be ended from the screen you are on is a real problem in a room with a
 * patient in it (§7).
 *
 * This is chrome, not a dashboard. The real dashboard — who is flagged, who is
 * behind, who has plateaued — is Phase 7 and renders inside this.
 *
 * The login screen deliberately sits OUTSIDE this layout: it must not show
 * navigation to places you cannot yet go.
 */

import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ClinicianSessionInfo } from '../api.js';
import styles from './ClinicianLayout.module.css';

/**
 * Where the doctor can go. Read top to bottom this is the whole clinician
 * surface, which is a useful thing for it to stay short.
 */
const NAV = [
  { to: '/clinician', label: 'Dashboard', end: true },
  { to: '/clinician/patients', label: 'Patients' },
  { to: '/clinician/preview', label: 'Exercise library' },
] as const;

export function ClinicianLayout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Shares the ['session'] key with the pages inside, so this is one request,
  // not one per screen.
  const session = useQuery({
    queryKey: ['session'],
    queryFn: () => api.get<ClinicianSessionInfo>('/auth/me'),
    retry: false,
  });

  const signOut = useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      queryClient.clear();
      navigate('/clinician/login', { replace: true });
    },
  });

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <img
          src="/brand/logo-small.png"
          alt="Pathways Brain &amp; Spinal Care"
          className={styles.logo}
          width={400}
          height={150}
        />

        <nav className={styles.nav} aria-label="Clinician">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={'end' in item ? item.end : false}
              className={({ isActive }) => (isActive ? styles.navLinkActive : styles.navLink)}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className={styles.account}>
          {session.data && (
            <span className={styles.who}>{session.data.clinician.displayName}</span>
          )}
          {session.isSuccess && (
            <button
              className={styles.signOut}
              type="button"
              onClick={() => signOut.mutate()}
              disabled={signOut.isPending}
            >
              {signOut.isPending ? 'Signing out…' : 'Sign out'}
            </button>
          )}
        </div>
      </header>

      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
}
